import { z } from 'zod'

import { uniqBy } from '@mntn-dev/utilities'
import { EnumBuilder, type ZodSimplify } from '@mntn-dev/utility-types'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import { NonEmptyStringSchema, TranslationsSchema } from './property.models.ts'
import { type TagId, TagIdSchema } from './unique-id.models.ts'

/**
 * Public tag categories
 */
export const [
  PublicTagCategories,
  PublicTagCategorySchema,
  PublicTagCategoryEnum,
] = EnumBuilder(
  'certification',
  'industry',
  'language',
  'location',
  'platform',
  'skill'
)
export type PublicTagCategory = ZodSimplify<typeof PublicTagCategorySchema>

/**
 * TagCategory
 */
export const [TagCategories, TagCategorySchema, TagCategoryEnum] = EnumBuilder(
  ...PublicTagCategories,
  'ethnicity',
  'gender',
  'uncategorized'
)
export type TagCategory = ZodSimplify<typeof TagCategorySchema>

/**
 * TagTranslations
 */
export const TagTranslationsSchema = TranslationsSchema('value', 'description')
export type TagTranslations = ZodSimplify<typeof TagTranslationsSchema>

/**
 * TagDomainSelectModel
 */
export const TagDomainSelectModelSchema = z.object({
  tagId: TagIdSchema,
  category: TagCategorySchema,
  key: NonEmptyStringSchema(128),
  value: NonEmptyStringSchema(),
  description: NonEmptyStringSchema().optional(),
  translations: TagTranslationsSchema.optional(),
})

export type TagDomainSelectModel = ZodSimplify<
  typeof TagDomainSelectModelSchema
>

/**
 * TagDomainInsertModel
 */
export const TagDomainInsertModelSchema = TagDomainSelectModelSchema.partial({
  tagId: true,
  value: true,
})

export type TagDomainInsertModel = ZodSimplify<
  typeof TagDomainInsertModelSchema
>

/**
 * TagDomainUpdateModel
 */
export const TagDomainUpdateModelSchema = TagDomainSelectModelSchema.omit({
  tagId: true,
})
  .extend({
    value: TagDomainSelectModelSchema.shape.value.nullable(),
    description: TagDomainSelectModelSchema.shape.description.nullable(),
    translations: TagDomainSelectModelSchema.shape.translations.nullable(),
  })
  .partial()

export type TagDomainUpdateModel = ZodSimplify<
  typeof TagDomainUpdateModelSchema
>

/**
 * TagDomainSelectModelFilter
 */
export const TagDomainSelectModelFiltersSchema = z
  .object({
    tagId: ModelFilterSchema(TagIdSchema),
    category: ModelFilterSchema(TagCategorySchema),
    key: ModelFilterSchema(TagDomainSelectModelSchema.shape.key.toLowerCase()),
    search: NonEmptyStringSchema(),
  })
  .partial()

export type TagDomainSelectModelFilters = ZodSimplify<
  typeof TagDomainSelectModelFiltersSchema
>

/**
 * TagFilter
 */
export const TagFilterModelSchema = ModelFilterSchema(TagIdSchema)
export type TagFilterModel = ZodSimplify<typeof TagFilterModelSchema>

export const TagFilterSchema = z.record(TagCategorySchema, TagFilterModelSchema)
export type TagFilter = ZodSimplify<typeof TagFilterSchema>

/**
 * TagsByCategoryMap
 */
export const TagsByCategoryMapSchema = z.record(
  TagCategorySchema,
  TagDomainSelectModelSchema.array()
)

export type TagsByCategoryMap = ZodSimplify<typeof TagsByCategoryMapSchema>

export const toTagArray = (
  tags: TagsByCategoryMap | undefined
): TagDomainSelectModel[] => (tags ? Object.values(tags).flat() : [])

// This is a function used to transform a list of tags into a map of unique tags by category
export const toTagsByCategoryMap = (
  tags: TagDomainSelectModel[] = []
): TagsByCategoryMap => {
  return TagsByCategoryMapSchema.parse({
    ...Object.groupBy(
      uniqBy(tags, (tag) => tag.tagId),
      (tag) => tag.category
    ),
  })
}

export const filterTagsByCategory = (
  tags: TagDomainSelectModel[] | undefined,
  category: TagDomainSelectModel['category']
): TagDomainSelectModel[] => {
  return tags
    ? uniqBy(tags, (tag) => tag.tagId).filter(
        (tag) => tag.category === category
      )
    : []
}

/**
 * TagIdsByCategoryMap
 * A lighter and semi-interopable version of TagsByCategoryMap that only contains tagId.
 * Useful as a tags prop type on domain update models when updating tags for objects.
 */
export const TagIdsByCategoryMapSchema = z.record(
  TagCategorySchema,
  TagDomainSelectModelSchema.pick({ tagId: true }).array()
)

export type TagIdsByCategoryMap = ZodSimplify<typeof TagIdsByCategoryMapSchema>

export const toTagIdsArray = (
  tags: TagIdsByCategoryMap | undefined
): TagId[] =>
  tags
    ? Object.values(tags)
        .flat()
        .map((tag) => tag.tagId)
    : []
